<template>
  <section
    v-resize.left="ownAsideResizeOptions"
    class="flex flex-col"
    :class="[
      isMyOwn
        ? (isMobileLayout
          ? 'h-full w-full min-w-0 max-w-none bg-layer-levels-1'
          : 'ml-px h-full min-w-[360px] max-w-[520px] shrink-0 border-l border-l-bor-nav bg-layer-levels-1 tablet:min-w-[300px] tablet:max-w-[300px]')
        : 'w-full',
      embedInScrollParent ? 'min-h-0' : 'grow min-h-0 overflow-hidden',
    ]"
    :style="ownAsideWidthStyle"
  >
    <header
      v-if="isMyOwn"
      class="flex h-14 shrink-0 items-center px-2"
      data-test="chat-gallery-own-header"
    >
      <wui-btn
        icon
        variant="neut"
        state="text"
        :size="40"
        class="shrink-0 rounded-10! text-fg-base-fg1!"
        prepend-icon="closeRegularMdM"
        data-test="chat-gallery-close"
        @click="emit('close')"
      >
        <span class="sr-only select-none">
          {{ $t('common.close') }}
        </span>
      </wui-btn>
      <h4 class="min-w-0 grow truncate pl-2 text-heading-sb-18-24 text-fg-base-fg3">
        {{ $t('common.savedMessages') }}
      </h4>
    </header>
    <div
      v-if="showStickyChrome"
      ref="stickyChromeRef"
      class="sticky top-0 z-10 mt-3.5 bg-layer-levels-1"
      data-test="chat-gallery-sticky-chrome"
    >
      <div
        v-if="showAttachmentTabs"
        class="px-4 py-[6px]"
        data-test="chat-gallery-tabs"
      >
        <wui-tabs
          ref="tabsContainerRef"
          class="min-w-0 w-full"
          scrollable
          :model-value="fileTypeForView"
          @update:model-value="onGalleryTabChange"
        >
          <wui-tabs-item
            v-for="tab in galleryTabs"
            :key="tab.type"
            :value="tab.type"
            :data-test="`chat-gallery-tab-${tab.type}`"
          >
            {{ tab.label }}
          </wui-tabs-item>
        </wui-tabs>
      </div>

      <div
        v-if="showGallerySearch"
        class="shrink-0 px-4 pb-2"
        data-test="chat-gallery-search"
      >
        <wui-input
          v-model="gallerySearch"
          :placeholder="gallerySearchPlaceholder"
          with-clear
          prepend-icon="searchM"
        />
      </div>

      <div
        v-if="showStickyDateHeader"
        class="flex h-[30px] items-center gap-3 px-6"
        data-test="chat-gallery-sticky-date"
      >
        <p class="min-w-0 grow text-body-m-14-20 text-fg-base-fg22">
          {{ stickyDateLabel }}
        </p>
        <label
          v-if="!isMyOwn"
          class="flex shrink-0 items-center gap-1.5 py-1"
          data-test="chat-gallery-only-mine"
        >
          <span class="text-body-r-14-20 text-fg-base-fg3">
            {{ $t('common.chatGalleryOnlyMine') }}
          </span>
          <wui-input-switch
            v-model="onlyMine"
            small
          />
        </label>
      </div>
    </div>
    <div
      class="relative flex flex-col px-4 pb-4"
      :class="embedInScrollParent ? undefined : 'grow min-h-0'"
      data-test="chat-gallery-content"
      @touchstart.passive="onGalleryContentTouchStart"
      @touchend.passive="onGalleryContentTouchEnd"
      @touchcancel.passive="onGalleryContentTouchCancel"
    >
      <chat-gallery-participants
        v-if="showParticipantsTab && room"
        v-show="fileTypeForView === GalleryTypes.PARTICIPANTS"
        :room="room"
        :embed-in-scroll-parent="embedInScrollParent"
      />
      <div
        v-if="isAttachmentsEnabled"
        v-show="fileTypeForView !== GalleryTypes.PARTICIPANTS"
        ref="attachmentsScrollRef"
        data-test="chat-gallery-attachments-scroll"
        class="flex flex-col"
        :class="embedInScrollParent ? undefined : 'grow min-h-0 overflow-y-auto pr-1 scrollbar-gutter-stable'"
      >
        <template v-if="groupedAttachments.length">
          <section
            v-for="(group, groupIndex) in groupedAttachments"
            :key="group.key"
            :ref="(element) => setGroupSectionRef(group.key, group.label, element)"
            class="flex flex-col"
            :data-gallery-group="group.key"
          >
            <p
              v-if="groupIndex > 0"
              class="my-1 flex h-[30px] items-center px-2 text-body-m-14-20 text-fg-base-fg22"
              data-test="chat-gallery-group-date"
            >
              {{ group.label }}
            </p>
            <div
              :class="isPhotoGridView
                ? 'grid grid-cols-3 gap-1'
                : (isVideosView ? 'flex flex-col gap-3' : 'flex flex-col gap-1')"
            >
              <template v-if="isPhotoGridView">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  v-touch.long="() => onMediaLongPress(item)"
                  class="group relative aspect-square cursor-pointer overflow-hidden rounded-4 bg-surf-neut-soft-def hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres"
                  :data-gallery-media-anchor="isMobileLayout ? item.id : undefined"
                  data-test="chat-gallery-media-item"
                  @click="onMediaClick(item)"
                  @contextmenu.prevent="onMediaContextMenu(item, $event)"
                >
                  <img
                    v-if="item.file"
                    :src="getAuthenticatedMediaUrl(`/files/${item.file.filecookie}`)"
                    :alt="item.file.filename"
                    class="pointer-events-none h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  >
                  <button
                    v-if="!isMobileLayout"
                    type="button"
                    class="absolute top-1 right-1 z-1 flex size-[28px] items-center justify-center rounded-4 bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    :class="{ 'opacity-100': activeMediaItem?.id === item.id && isMediaMenuShowed }"
                    :data-gallery-media-anchor="item.id"
                    data-test="chat-gallery-media-menu"
                    :aria-label="$t('common.more')"
                    @click.stop="openMediaMenu(item, undefined, $event)"
                  >
                    <wui-icon
                      name="kebabVerticalM"
                      class="size-4!"
                    />
                  </button>
                </div>
              </template>
              <template v-else-if="isVideosView">
                <chat-gallery-video-item
                  v-for="item in group.items"
                  :key="item.id"
                  :title="getVideoTitle(item)"
                  :sender-name="getVideoSenderName(item)"
                  :meta-label="getVideoMeta(item)"
                  :preview-src="getVideoPreviewSrc(item)"
                  :duration-seconds="getVideoDurationSeconds(item)"
                  :is-event-recording="isEventRecordingVideo(item)"
                  :show-kebab="!isMobileLayout"
                  :is-menu-active="activeMediaItem?.id === item.id && isMediaMenuShowed"
                  :anchor-id="item.id"
                  @open="onMediaClick(item)"
                  @open-menu="(event) => openMediaMenu(item, undefined, event)"
                  @contextmenu="(event) => onMediaContextMenu(item, event)"
                  @long-press="onMediaLongPress(item)"
                />
              </template>
              <template v-else-if="isLinksView">
                <chat-gallery-link-list-item
                  v-for="item in group.items"
                  :key="item.id"
                  :title="getLinkTitle(item.content ?? '')"
                  :subtitle="getLinkMeta(item)"
                  :show-kebab="!isMobileLayout"
                  :is-menu-active="activeMediaItem?.id === item.id && isMediaMenuShowed"
                  :anchor-id="item.id"
                  @open="onMediaClick(item)"
                  @open-menu="(event) => openMediaMenu(item, undefined, event)"
                  @contextmenu="(event) => onMediaContextMenu(item, event)"
                  @long-press="onMediaLongPress(item)"
                />
              </template>
              <template v-else-if="isFilesView">
                <chat-gallery-file-list-item
                  v-for="item in group.items"
                  :key="item.id"
                  :title="item.file ? getGalleryFileDisplayName(item.file) : ''"
                  :subtitle="item.file ? getFileSubtitle(item) : ''"
                  :mime="item.file?.filemime ?? ''"
                  :cookie="item.file?.filecookie ?? ''"
                  :file-name="item.file?.filename ?? ''"
                  :is-voice="Boolean(item.file && isGalleryVoiceFile(item.file))"
                  :show-kebab="!isMobileLayout"
                  :is-menu-active="activeMediaItem?.id === item.id && isMediaMenuShowed"
                  :anchor-id="item.id"
                  @open="onMediaClick(item)"
                  @open-menu="(event) => openMediaMenu(item, undefined, event)"
                  @contextmenu="(event) => onMediaContextMenu(item, event)"
                  @long-press="onMediaLongPress(item)"
                />
              </template>
              <template v-else>
                <button
                  v-for="item in group.items"
                  :key="item.id"
                  class="w-full rounded-10 bg-surf-neut-soft-def hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres p-2 text-left"
                  @click="onAttachmentClick(item)"
                >
                  <div class="flex items-center gap-2">
                    <div class="min-w-0">
                      <p
                        v-if="item.file"
                        class="truncate text-body-r-14-20 text-fg-base-fg3"
                      >
                        {{ item.file.filename }}
                      </p>
                    </div>
                  </div>
                </button>
              </template>
            </div>
          </section>
        </template>

        <p
          v-else-if="isFilteredEmpty"
          class="px-3 py-8 text-center text-body-r-15-22 text-fg-base-fg17"
          data-test="chat-gallery-filtered-empty"
        >
          {{ $t('common.chatGalleryNothingFound') }}
        </p>
        <p
          v-else-if="isFullEmpty"
          class="px-3 py-16 text-center text-body-r-15-22 text-fg-base-fg17"
          data-test="chat-gallery-empty"
        >
          {{ emptyStateText }}
        </p>

        <div
          v-if="showLoadMoreBar"
          class="flex flex-col items-center px-4 py-4"
        >
          <wui-btn
            variant="neut"
            state="filled"
            :loading="isGalleryLoading"
            :disabled="isGalleryLoading || !(canLoadMoreHistory || hasPendingFiles)"
            data-test="chat-gallery-load-more"
            @click="onLoadMoreClick"
          >
            {{ $t('common.chatGalleryShowMore') }}
          </wui-btn>
        </div>

        <div
          ref="loadMoreSentinelRef"
          class="h-1 w-full shrink-0"
          aria-hidden="true"
          data-test="chat-gallery-load-more-sentinel"
        />
      </div>

      <div
        v-if="isMediaMenuShowed && activeMediaItem"
        ref="mediaMenuPanelRef"
        v-click-outside="mediaMenuClickOutside"
        class="fixed z-9999 w-max"
        :style="mediaMenuPositionStyle"
        data-test="chat-gallery-media-context-menu"
      >
        <wui-card
          class="min-w-[240px] bg-elev-context-menu-bg!"
          state="text"
          shadow="overflow"
        >
          <wui-list>
            <template v-if="activeMediaItem">
              <wui-list-item
                icon="messageArrowRightM"
                data-test="chat-gallery-media-go-to-message"
                @click="onMediaGoToMessage"
              >
                <p>{{ $t('common.goToMessage') }}</p>
              </wui-list-item>
              <wui-list-item
                v-if="!isEventRecordingVideo(activeMediaItem)"
                icon="forwardRight"
                data-test="chat-gallery-media-forward"
                @click="onMediaForward"
              >
                <p>{{ mediaForwardLabel }}</p>
              </wui-list-item>
            </template>
            <wui-list-item
              v-if="isActiveLinkItem"
              icon="linkM"
              data-test="chat-gallery-media-copy-link"
              @click="onMediaCopyLink"
            >
              <p>{{ $t('common.chatGalleryCopyLink') }}</p>
            </wui-list-item>
            <wui-list-item
              v-else
              icon="downloadM"
              data-test="chat-gallery-media-download"
              @click="onMediaDownload"
            >
              <p>{{ $t('common.download') }}</p>
            </wui-list-item>
            <template v-if="canDeleteActiveMedia">
              <hr class="mx-1 my-1 border-t-bor-nav">
              <wui-list-item
                icon="trashM"
                variant="negative"
                data-test="chat-gallery-media-delete"
                @click="onMediaDelete"
              >
                <p>{{ $t('common.remove') }}</p>
              </wui-list-item>
            </template>
          </wui-list>
        </wui-card>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  ClickOutside as vClickOutside,
  useDialog,
  useRenderNotifier,
  WuiBtn,
  WuiCard,
  WuiIcon,
  WuiInput,
  WuiInputSwitch,
  WuiList,
  WuiListItem,
  WuiTabs,
  WuiTabsItem,
} from '@wui/common-library'
import {
  HistoryMessageItem,
  IMFile,
  isOutgoingMessage,
  isTextMessage,
  MessageCallAppointmentEnd,
  MessageCallReceived,
  MessageCallRecordCompleted,
  MessageIncomingEvent,
  MessageOutgoingEvent,
} from '@wui/im'
import { unix } from 'dayjs'
import { ComponentPublicInstance, computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'


import { ChatForwardMessage } from '@/widgets/chat-forward-message-widget'
import {
  isImageAttachment,
  isVideoAttachment,
  isAudioAttachment,
  useOpenAttachmentViewer,
} from '@/widgets/media-with-thread-chat'

import { useStoreContacts } from '@/entities/contact'
import { chatModel, usePinEventsStore } from '@/entities/messages'
import { useRoom } from '@/entities/use-room'
import { VOICE_MESSAGE_FILE_PREFIX } from '@/entities/voice-message'

import { useConfirmDialog } from '@/shared/components/dialogs'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { isMobileLayout } from '@/shared/composables/use-mobile'
import { useRightAsideResizeBinding } from '@/shared/composables/use-right-aside-width'
import { vResize } from '@/shared/directives/v-resize'
import { vTouch } from '@/shared/directives/v-touch'
import { AttachmentItem, GalleryTypes } from '@/shared/types/chat-aside'
import { MIMEtypes } from '@/shared/types/files'
import { Room } from '@/shared/types/store'
import { bytesConverter } from '@/shared/utils/bytes-converter'
import { download as downloadAttachment } from '@/shared/utils/download-files'
import sort from '@/shared/utils/sort'
import { getMessageLinkMarkup } from '@/shared/utils/text-entities'
import { getAuthenticatedMediaUrl, getPathNameFromURL } from '@/shared/utils/url'

import { CHAT_GALLERY_ATTACHMENTS_ENABLED } from '../config'
import { groupGalleryItemsByDate, useChatGalleryHistory } from '../lib'

import ChatGalleryFileListItem from './chat-gallery-file-list-item.vue'
import ChatGalleryLinkListItem from './chat-gallery-link-list-item.vue'
import ChatGalleryParticipants from './chat-gallery-participants.vue'
import ChatGalleryVideoItem from './chat-gallery-video-item.vue'

const isAttachmentsEnabled = CHAT_GALLERY_ATTACHMENTS_ENABLED

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'go-to-message', eventId: number): void
}>()

const props = defineProps<{
  pServed: string,
  room?: Room,
  isMyOwn?: boolean,
  embedInScrollParent?: boolean,
}>()

const { t } = useLocalization()
const { openAttachmentViewer } = useOpenAttachmentViewer()
const { showDialog } = useDialog()
const { findRoomMessages } = chatModel.useChatMessageStore()
const { show: showConfirm } = useConfirmDialog()
const { notifyError, notifySuccess } = useRenderNotifier()
const roomApi = useRoom()
const { getPinEventsForRoom } = usePinEventsStore()
const { resizeOptions: ownAsideResizeOptions, asideWidthStyle: ownAsideWidthStyle } = useRightAsideResizeBinding(
  () => Boolean(props.isMyOwn) && !props.embedInScrollParent && !isMobileLayout.value,
)
const { findByPServed } = useStoreContacts()
const { currentUser } = useCurrentUser()

const {
  allMessages,
  canLoadMoreHistory,
  hasMoreHistory,
  hasPendingFiles,
  isLoading: isGalleryLoading,
  loadOlder,
} = useChatGalleryHistory(toRef(props, 'pServed'))

const showLoadMoreBar = computed(() => {
  if (!isAttachmentsEnabled || fileTypeForView.value === GalleryTypes.PARTICIPANTS) {
    return false
  }

  return hasMoreHistory.value || isGalleryLoading.value || hasPendingFiles.value
})

const onlyMine = ref(false)
const linksSearch = ref('')
const videosSearch = ref('')
const filesSearch = ref('')
const textMessages = computed(() => allMessages.value.filter(isTextMessage))
const callRecordMessages = computed(() => {
  return allMessages.value.filter((message): message is HistoryMessageItem<MessageCallRecordCompleted> => {
    return (message.item as MessageCallReceived)?.event === 'call_record_completed'
  })
})

const isEventRecordingVideo = (item: AttachmentItem) => item.content === 'event-recording'

const getVideoTitle = (item: AttachmentItem) => {
  if (isEventRecordingVideo(item)) {
    return item.file?.filename || t('calls.callRecord')
  }

  return item.file?.filename || t('common.videos')
}

const getVideoSenderName = (item: AttachmentItem) => {
  if (isEventRecordingVideo(item)) {
    return ''
  }

  if (isOutgoingMessage(item.event)) {
    return currentUser.value?.name || t('common.noName')
  }

  const executor = 'executor' in item.event.item ? item.event.item.executor : undefined
  if (!executor) {
    return t('common.noName')
  }

  return findByPServed(executor)?.name || t('common.noName')
}

const getVideoMeta = (item: AttachmentItem) => {
  const date = unix(item.event.timestamp).tz().format('D MMMM YYYY')
  const size = typeof item.file?.filesize === 'number'
    ? String(bytesConverter(item.file.filesize))
    : ''

  return [date, size].filter(Boolean).join(' • ')
}

const getVideoPreviewSrc = (item: AttachmentItem) => {
  if (!item.file?.filecookie) {
    return ''
  }

  const cookie = item.file.filecookie
  const base = cookie.startsWith('http') || cookie.startsWith('/')
    ? getAuthenticatedMediaUrl(cookie.startsWith('http') ? getPathNameFromURL(cookie) : cookie)
    : getAuthenticatedMediaUrl(`/files/${cookie}`)

  return `${base}#t=0.001`
}

const getVideoDurationSeconds = (item: AttachmentItem) => {
  if (!isEventRecordingVideo(item)) {
    return 0
  }

  const duration = (item.event.item as unknown as MessageCallRecordCompleted).duration
  return typeof duration === 'number' && duration > 0 ? duration : 0
}

const getCallRecordFileCookie = (url: string) => {
  const pathName = url.includes('://') ? getPathNameFromURL(url) : url
  return pathName.replace(/^\/files\//, '').replace(/^\//, '')
}
const showParticipantsTab = computed(() => Boolean(props.room?.isGroup))

const galleryTabs = computed(() => {
  const tabs: { type: GalleryTypes, label: string }[] = []
  if (showParticipantsTab.value) {
    tabs.push({ type: GalleryTypes.PARTICIPANTS, label: t('common.participants') })
  }
  if (isAttachmentsEnabled) {
    tabs.push(
      { type: GalleryTypes.PHOTO, label: t('common.images') },
      { type: GalleryTypes.VIDEO, label: t('common.videos') },
      { type: GalleryTypes.FILE, label: t('common.files') },
      { type: GalleryTypes.LINK, label: t('common.links') },
    )
  }
  return tabs
})

const showAttachmentTabs = computed(() => galleryTabs.value.length > 0)

const tabsForView = computed(() => galleryTabs.value.map((tab) => tab.type))

const attachmentItemsByType = computed(() => {
  const grouped = new Map<GalleryTypes, AttachmentItem[]>([
    [GalleryTypes.PHOTO, []],
    [GalleryTypes.VIDEO, []],
    [GalleryTypes.FILE, []],
    [GalleryTypes.LINK, []],
  ])

  const sortedMessages = textMessages.value.slice().sort(sort.chatEvents.byTimestamp)
  sortedMessages.forEach((message) => {
    message.item.files?.forEach((file, index) => {
      const type = getFileCategory(file)
      grouped.get(type)?.push({
        id: `${message.eventId}-${file.filecookie}-${index}`,
        event: message as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
        file,
      })
    })

    const links = getMessageLinkMarkup(message.item)
    links.forEach((link, index) => {
      grouped.get(GalleryTypes.LINK)?.push({
        id: `${message.eventId}-link-${index}`,
        event: message as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
        content: link,
      })
    })
  })

  callRecordMessages.value
    .slice()
    .sort(sort.chatEvents.byTimestamp)
    .forEach((message) => {
      const url = message.item.callRecordUrl ?? ''
      if (!url) {
        return
      }

      const filename = url.split('/').pop()?.split('?')[0] || t('calls.callRecord')
      const extension = filename.includes('.')
        ? filename.slice(filename.lastIndexOf('.') + 1).toLowerCase()
        : ''
      const filemime = MIMEtypes.get(extension) || 'video/mp4'
      const file = {
        filecookie: getCallRecordFileCookie(url),
        filename,
        filemime,
      }

      const galleryType = isAudioAttachment(file) ? GalleryTypes.FILE : GalleryTypes.VIDEO
      grouped.get(galleryType)?.push({
        id: `${message.eventId}-call-record`,
        event: message as unknown as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
        file,
        content: 'event-recording',
      })
    })

  return grouped
})

const attachmentsForActiveType = computed(() => {
  return attachmentItemsByType.value.get(fileTypeForView.value) ?? []
})

const searchedAttachments = computed(() => {
  const items = attachmentsForActiveType.value

  if (fileTypeForView.value === GalleryTypes.LINK) {
    const query = linksSearch.value.trim().toLowerCase()
    if (!query) {
      return items
    }

    return items.filter((item) => {
      const title = getLinkTitle(item.content ?? '').toLowerCase()
      return title.includes(query)
    })
  }

  if (fileTypeForView.value === GalleryTypes.VIDEO) {
    const query = videosSearch.value.trim().toLowerCase()
    if (!query) {
      return items
    }

    return items.filter((item) => {
      const title = getVideoTitle(item).toLowerCase()
      const sender = getVideoSenderName(item).toLowerCase()
      return title.includes(query) || sender.includes(query)
    })
  }

  if (fileTypeForView.value === GalleryTypes.FILE) {
    const query = filesSearch.value.trim().toLowerCase()
    if (!query) {
      return items
    }

    return items.filter((item) => {
      const title = item.file
        ? getGalleryFileDisplayName(item.file).toLowerCase()
        : ''
      const filename = (item.file?.filename ?? '').toLowerCase()
      const sender = getLinkSenderName(item.event).toLowerCase()
      return title.includes(query) || filename.includes(query) || sender.includes(query)
    })
  }

  return items
})

const activeAttachments = computed(() => {
  if (!onlyMine.value) {
    return searchedAttachments.value
  }

  return searchedAttachments.value.filter((item) => {
    if (isEventRecordingVideo(item)) {
      return false
    }

    return isOutgoingMessage(item.event)
  })
})

const groupedAttachments = computed(() => {
  return groupGalleryItemsByDate(
    activeAttachments.value.map((item) => ({
      ...item,
      timestamp: item.event.timestamp,
    })),
  )
})

const isPhotoGridView = computed(() => fileTypeForView.value === GalleryTypes.PHOTO)

const isVideosView = computed(() => fileTypeForView.value === GalleryTypes.VIDEO)

const isLinksView = computed(() => fileTypeForView.value === GalleryTypes.LINK)

const isFilesView = computed(() => fileTypeForView.value === GalleryTypes.FILE)

const showGallerySearch = computed(() => {
  return isLinksView.value || isVideosView.value || isFilesView.value
})

const gallerySearch = computed({
  get: () => {
    if (isLinksView.value) {
      return linksSearch.value
    }
    if (isVideosView.value) {
      return videosSearch.value
    }
    if (isFilesView.value) {
      return filesSearch.value
    }
    return ''
  },
  set: (value: string) => {
    if (isLinksView.value) {
      linksSearch.value = value
      return
    }
    if (isVideosView.value) {
      videosSearch.value = value
      return
    }
    if (isFilesView.value) {
      filesSearch.value = value
    }
  },
})

const gallerySearchPlaceholder = computed(() => {
  if (isVideosView.value) {
    return t('common.chatGallerySearchVideos')
  }
  if (isFilesView.value) {
    return t('common.chatGallerySearchFiles')
  }
  return t('common.chatGallerySearchLinks')
})

const isFullEmpty = computed(() => {
  return fileTypeForView.value !== GalleryTypes.PARTICIPANTS
    && attachmentsForActiveType.value.length === 0
    && !linksSearch.value.trim()
    && !videosSearch.value.trim()
    && !filesSearch.value.trim()
})

const isFilteredEmpty = computed(() => {
  return !isFullEmpty.value
    && fileTypeForView.value !== GalleryTypes.PARTICIPANTS
    && groupedAttachments.value.length === 0
})

const emptyStateText = computed(() => {
  switch (fileTypeForView.value) {
  case GalleryTypes.PHOTO:
    return t('common.chatGalleryNoImages')
  case GalleryTypes.VIDEO:
    return t('common.chatGalleryNoVideos')
  case GalleryTypes.FILE:
    return t('common.chatGalleryNoFiles')
  case GalleryTypes.LINK:
    return t('common.chatGalleryNoLinks')
  default:
    return t('common.nothingFound')
  }
})

const showStickyDateHeader = computed(() => {
  return isAttachmentsEnabled
    && fileTypeForView.value !== GalleryTypes.PARTICIPANTS
    && groupedAttachments.value.length > 0
})

const showStickyChrome = computed(() => {
  return showAttachmentTabs.value
    || showGallerySearch.value
    || showStickyDateHeader.value
})

const tabsContainerRef = ref<ComponentPublicInstance | HTMLElement | null>(null)
const attachmentsScrollRef = ref<HTMLElement | null>(null)
const loadMoreSentinelRef = ref<HTMLElement | null>(null)
const stickyChromeRef = ref<HTMLElement | null>(null)
const stickyDateLabel = ref('')
const groupSectionByKey = new Map<string, { el: HTMLElement, label: string }>()
const isMediaMenuShowed = ref(false)
const activeMediaItem = ref<AttachmentItem | null>(null)
const suppressMediaClick = ref(false)
let loadMoreObserver: IntersectionObserver | null = null
let detachStickyDateScroll: (() => void) | null = null

const isActiveLinkItem = computed(() => {
  const item = activeMediaItem.value
  return Boolean(item?.content && item.content !== 'event-recording' && !item.file)
})

const canDeleteActiveMedia = computed(() => {
  if (!activeMediaItem.value) {
    return false
  }

  return isOutgoingMessage(activeMediaItem.value.event) && !isEventRecordingVideo(activeMediaItem.value)
})

const setGroupSectionRef = (key: string, label: string, element: Element | ComponentPublicInstance | null) => {
  const el = element instanceof HTMLElement
    ? element
    : element && '$el' in element && element.$el instanceof HTMLElement
      ? element.$el
      : null

  if (el) {
    groupSectionByKey.set(key, { el, label })
    return
  }

  groupSectionByKey.delete(key)
}

const updateStickyDateLabel = () => {
  const groups = groupedAttachments.value
  if (!groups.length) {
    stickyDateLabel.value = ''
    return
  }

  const stickyBottom = stickyChromeRef.value?.getBoundingClientRect().bottom ?? 0
  let currentLabel = groups[0]!.label

  for (const group of groups) {
    const entry = groupSectionByKey.get(group.key)
    if (!entry) {
      continue
    }

    if (entry.el.getBoundingClientRect().top <= stickyBottom + 4) {
      currentLabel = group.label
    }
  }

  stickyDateLabel.value = currentLabel
}

const getGalleryScrollRoot = (): HTMLElement | null => {
  if (props.embedInScrollParent) {
    return findScrollParent(stickyChromeRef.value)
      ?? findScrollParent(attachmentsScrollRef.value)
  }

  return attachmentsScrollRef.value
}

const setupStickyDateScroll = () => {
  detachStickyDateScroll?.()
  detachStickyDateScroll = null

  if (!showStickyDateHeader.value) {
    stickyDateLabel.value = ''
    return
  }

  stickyDateLabel.value = groupedAttachments.value[0]?.label ?? ''

  const root = getGalleryScrollRoot()
  if (!root) {
    return
  }

  const onScroll = () => updateStickyDateLabel()
  root.addEventListener('scroll', onScroll, { passive: true })
  detachStickyDateScroll = () => root.removeEventListener('scroll', onScroll)
  void nextTick(updateStickyDateLabel)
}

const MEDIA_MENU_MIN_WIDTH_PX = 240
const MEDIA_MENU_VIEWPORT_PADDING_PX = 8

const mediaMenuPanelRef = ref<HTMLElement | null>(null)
const mediaMenuAnchorEl = ref<HTMLElement | null>(null)
const mediaMenuPositionStyle = ref<Record<string, string>>({})
let detachMediaMenuScrollClose: (() => void) | null = null

const mediaForwardLabel = computed(() => {
  if (fileTypeForView.value === GalleryTypes.VIDEO) {
    return t('common.chatGalleryForwardVideo')
  }

  if (fileTypeForView.value === GalleryTypes.FILE) {
    return t('common.chatGalleryForwardFile')
  }

  if (fileTypeForView.value === GalleryTypes.LINK) {
    return t('common.chatGalleryForwardLink')
  }

  return t('common.chatGalleryForwardImage')
})

/** filecookie в id ломает CSS.escape-селектор — сравниваем через dataset. */
const findMediaMenuAnchorEl = (itemId: string) => {
  const nodes = document.querySelectorAll<HTMLElement>('[data-gallery-media-anchor]')
  return Array.from(nodes).find(el => el.dataset.galleryMediaAnchor === itemId) ?? null
}

const resolveMediaMenuAnchorEl = (item: AttachmentItem, event?: Event) => {
  const current = event?.currentTarget
  if (current instanceof HTMLElement) {
    if (current.dataset.galleryMediaAnchor === item.id) {
      return current
    }
    const nested = current.querySelector<HTMLElement>('[data-gallery-media-anchor]')
    if (nested?.dataset.galleryMediaAnchor === item.id) {
      return nested
    }
  }

  return findMediaMenuAnchorEl(item.id)
}

const updateMediaMenuPosition = () => {
  const anchor = mediaMenuAnchorEl.value
    ?? (activeMediaItem.value ? findMediaMenuAnchorEl(activeMediaItem.value.id) : null)
  if (!anchor) {
    return
  }

  mediaMenuAnchorEl.value = anchor
  const rect = anchor.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) {
    return
  }

  const menuWidth = mediaMenuPanelRef.value?.offsetWidth || MEDIA_MENU_MIN_WIDTH_PX
  const menuHeight = mediaMenuPanelRef.value?.offsetHeight || 0
  const padding = MEDIA_MENU_VIEWPORT_PADDING_PX
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.left - menuWidth
  let top = rect.top

  if (left < padding) {
    left = rect.right
  }

  if (menuHeight > 0 && top + menuHeight > viewportHeight - padding) {
    top = viewportHeight - padding - menuHeight
  }

  left = Math.min(Math.max(padding, left), Math.max(padding, viewportWidth - padding - menuWidth))
  top = Math.min(Math.max(padding, top), Math.max(padding, viewportHeight - padding - menuHeight))

  mediaMenuPositionStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  }
}

const detachMediaMenuListeners = () => {
  detachMediaMenuScrollClose?.()
  detachMediaMenuScrollClose = null
  window.removeEventListener('resize', updateMediaMenuPosition)
}

const attachMediaMenuListeners = () => {
  detachMediaMenuListeners()
  const root = getGalleryScrollRoot()
  const onScroll = () => {
    hideMediaMenu()
  }
  root?.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', updateMediaMenuPosition)
  detachMediaMenuScrollClose = () => {
    root?.removeEventListener('scroll', onScroll)
  }
}

const hideMediaMenu = () => {
  isMediaMenuShowed.value = false
  mediaMenuAnchorEl.value = null
  detachMediaMenuListeners()
}

const mediaMenuClickOutside = {
  handler: () => hideMediaMenu(),
  closeConditional: (event: Event) => {
    const target = event.target
    if (!(target instanceof Element)) {
      return true
    }
    return !target.closest('[data-gallery-media-anchor]')
  },
}

const openMediaMenu = async (
  item: AttachmentItem,
  options?: { suppressClick?: boolean },
  event?: Event,
) => {
  activeMediaItem.value = item
  mediaMenuAnchorEl.value = resolveMediaMenuAnchorEl(item, event)
  if (options?.suppressClick) {
    suppressMediaClick.value = true
  }
  isMediaMenuShowed.value = true
  await nextTick()
  if (!mediaMenuAnchorEl.value) {
    mediaMenuAnchorEl.value = findMediaMenuAnchorEl(item.id)
  }
  updateMediaMenuPosition()
  await nextTick()
  updateMediaMenuPosition()
  attachMediaMenuListeners()
}

const onMediaClick = (item: AttachmentItem) => {
  if (suppressMediaClick.value) {
    suppressMediaClick.value = false
    return
  }

  void onAttachmentClick(item)
}

const onMediaContextMenu = (item: AttachmentItem, event?: MouseEvent) => {
  if (isMobileLayout.value) {
    return
  }

  openMediaMenu(item, { suppressClick: true }, event)
}

const onMediaLongPress = (item: AttachmentItem) => {
  openMediaMenu(item, { suppressClick: true })
}

const onMediaGoToMessage = () => {
  if (!activeMediaItem.value) {
    return
  }

  onGoToMessage(activeMediaItem.value)
  hideMediaMenu()
}

const onMediaForward = () => {
  if (!activeMediaItem.value) {
    return
  }

  onForwardAttachment(activeMediaItem.value)
  hideMediaMenu()
}

const onMediaDownload = () => {
  if (!activeMediaItem.value?.file) {
    return
  }

  downloadAttachment(activeMediaItem.value.file, '/files/')
  hideMediaMenu()
}

const onMediaCopyLink = () => {
  if (!activeMediaItem.value) {
    return
  }

  void onCopyLink(activeMediaItem.value)
  hideMediaMenu()
}

const onMediaDelete = () => {
  if (!activeMediaItem.value) {
    return
  }

  const item = activeMediaItem.value
  hideMediaMenu()
  void onDeleteAttachment(item)
}

const onGalleryTabChange = (value: string | number) => {
  fileTypeForView.value = value as GalleryTypes
}

const CONTENT_SWIPE_THRESHOLD_PX = 60
const CONTENT_SWIPE_HORIZONTAL_RATIO = 1.2

let contentSwipeStartX = 0
let contentSwipeStartY = 0
let isContentSwipeTracking = false

const switchToAdjacentTab = (direction: 1 | -1) => {
  const tabs = tabsForView.value
  const currentIndex = tabs.indexOf(fileTypeForView.value)
  if (currentIndex < 0) {
    return
  }

  const nextTab = tabs[currentIndex + direction]
  if (nextTab === undefined) {
    return
  }

  fileTypeForView.value = nextTab
}

const onGalleryContentTouchStart = (event: TouchEvent) => {
  if (!isMobileLayout.value || event.touches.length !== 1) {
    isContentSwipeTracking = false
    return
  }

  const touch = event.touches[0]
  if (!touch) {
    return
  }

  contentSwipeStartX = touch.clientX
  contentSwipeStartY = touch.clientY
  isContentSwipeTracking = true
}

const onGalleryContentTouchCancel = () => {
  isContentSwipeTracking = false
}

const onGalleryContentTouchEnd = (event: TouchEvent) => {
  if (!isContentSwipeTracking || !isMobileLayout.value) {
    return
  }

  isContentSwipeTracking = false

  if (isMediaMenuShowed.value) {
    return
  }

  const touch = event.changedTouches[0]
  if (!touch) {
    return
  }

  const deltaX = touch.clientX - contentSwipeStartX
  const deltaY = touch.clientY - contentSwipeStartY
  const absDeltaX = Math.abs(deltaX)
  const absDeltaY = Math.abs(deltaY)

  if (absDeltaX < CONTENT_SWIPE_THRESHOLD_PX) {
    return
  }

  if (absDeltaX < absDeltaY * CONTENT_SWIPE_HORIZONTAL_RATIO) {
    return
  }

  // Swipe left → next tab, swipe right → previous tab
  switchToAdjacentTab(deltaX < 0 ? 1 : -1)
}

const onAttachmentClick = async (item: AttachmentItem) => {
  if (fileTypeForView.value === GalleryTypes.LINK && item.content && item.content !== 'event-recording') {
    const href = getLinkHref(item.content)
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer')
    }
    return
  }

  if (!item.file) {
    return
  }

  if (isAudioAttachment(item.file)) {
    window.open(getAuthenticatedMediaUrl(`/files/${item.file.filecookie}`), '_blank', 'noopener,noreferrer')
    return
  }

  await openAttachmentViewer(item.file, item.event)
}

const getLinkTitle = (link: string) => {
  const href = getLinkHref(link)
  if (href) {
    return href
  }
  return link.replace(/<[^>]*>/g, '').trim()
}

const getLinkHref = (link: string) => {
  const match = link.match(/href="([^"]+)"/i)
  return match?.[1] ?? null
}

const getLinkSenderName = (event: AttachmentItem['event']) => {
  if (isOutgoingMessage(event)) {
    return currentUser.value?.name || t('common.noName')
  }

  const executor = 'executor' in event.item ? event.item.executor : undefined
  if (!executor) {
    return t('common.noName')
  }

  return findByPServed(executor)?.name || t('common.noName')
}

const getLinkMeta = (item: AttachmentItem) => {
  const name = getLinkSenderName(item.event)
  const date = unix(item.event.timestamp).tz().format('D MMMM YYYY')
  return `${name}, ${date}`
}

const getFileSubtitle = (item: AttachmentItem) => {
  const name = getLinkSenderName(item.event)
  const date = unix(item.event.timestamp).tz().format('D MMMM YYYY')
  const size = typeof item.file?.filesize === 'number'
    ? String(bytesConverter(item.file.filesize))
    : ''
  const meta = [date, size].filter(Boolean).join(' • ')

  return `${name}, ${meta}`
}


const resolveGoToMessageEventId = (item: AttachmentItem) => {
  if (!isEventRecordingVideo(item)) {
    return item.event.eventId
  }

  const confId = (item.event.item as unknown as MessageCallRecordCompleted).confId
  if (!confId) {
    return item.event.eventId
  }

  const appointmentEnd = findRoomMessages(props.pServed).find((message) => {
    const callItem = message.item as MessageCallAppointmentEnd
    return callItem.event === 'appointment_end' && callItem.confId === confId
  })

  return appointmentEnd?.eventId ?? item.event.eventId
}

const onGoToMessage = (item: AttachmentItem) => {
  emit('go-to-message', resolveGoToMessageEventId(item))
}

const onForwardAttachment = (item: AttachmentItem) => {
  showDialog(ChatForwardMessage, {
    chatEvents: [item.event],
    hasOverlay: true,
  })
}

const onCopyLink = async (item: AttachmentItem) => {
  const href = getLinkHref(item.content ?? '')
  if (!href) {
    return
  }

  try {
    await navigator.clipboard.writeText(href)
    notifySuccess(t('common.linkCopiedToClipboard'))
  } catch {
    notifyError(t('common.unknownError'))
  }
}

const onDeleteAttachment = async (item: AttachmentItem) => {
  const confirmed = await showConfirm({
    title: t('common.remove'),
    message: t('common.areYouSureYouWantToDelete'),
    confirmLabel: t('common.remove'),
  })
  if (!confirmed) {
    return
  }

  try {
    const deleted = await roomApi.delete(item.event.eventId, props.pServed)
    if (!deleted) {
      return
    }

    const pinEvents = getPinEventsForRoom(props.pServed)
    const pinEvent = pinEvents?.find((pin) => pin.eventId === item.event.eventId)
    if (pinEvent) {
      await roomApi.deletePin(pinEvent.eventId, props.pServed)
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const isGalleryVoiceFile = (file: IMFile) => {
  return file.filename.toLowerCase().startsWith(VOICE_MESSAGE_FILE_PREFIX)
}

/** Voice messages are often `voice-message.webm` — extension alone maps to video. */
const isGalleryAudioOrVoiceFile = (file: IMFile) => {
  return isAudioAttachment(file) || isGalleryVoiceFile(file)
}

const getGalleryFileDisplayName = (file: IMFile) => {
  if (isGalleryVoiceFile(file)) {
    return t('mainAside.voiceMessage')
  }

  return file.filename
}

const getFileCategory = (file: IMFile) => {
  if (isImageAttachment(file)) {
    return GalleryTypes.PHOTO
  }

  if (isGalleryAudioOrVoiceFile(file)) {
    return GalleryTypes.FILE
  }

  if (isVideoAttachment(file)) {
    return GalleryTypes.VIDEO
  }

  return GalleryTypes.FILE
}

const fileTypeForView = ref(GalleryTypes.PARTICIPANTS)

const resolveTabsRootEl = (): HTMLElement | null => {
  const root = tabsContainerRef.value
  if (!root) {
    return null
  }
  if (root instanceof HTMLElement) {
    return root
  }
  return root.$el instanceof HTMLElement ? root.$el : null
}

const scrollToActiveTab = () => {
  const tabsRoot = resolveTabsRootEl()
  const activeTab = tabsRoot?.querySelector<HTMLElement>(
    `[data-test="chat-gallery-tab-${fileTypeForView.value}"]`,
  )
  if (!activeTab) {
    return
  }

  activeTab.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  })
}

const findScrollParent = (el: HTMLElement | null) => {
  let node = el?.parentElement ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node
    }
    node = node.parentElement
  }
  return null
}

let isSentinelIntersecting = false

const onLoadMoreClick = () => {
  // One batch per click; mark sentinel as already seen so IO does not immediately re-fire.
  isSentinelIntersecting = true
  void loadOlder()
}

const setupLoadMoreObserver = () => {
  loadMoreObserver?.disconnect()
  loadMoreObserver = null

  if (!isAttachmentsEnabled || !loadMoreSentinelRef.value) {
    return
  }

  if (fileTypeForView.value === GalleryTypes.PARTICIPANTS) {
    return
  }

  // Empty tabs keep the button only — sentinel would always intersect and exhaust history.
  if (!groupedAttachments.value.length) {
    return
  }

  if (!hasMoreHistory.value && !hasPendingFiles.value) {
    return
  }

  const root = props.embedInScrollParent
    ? findScrollParent(loadMoreSentinelRef.value)
    : attachmentsScrollRef.value

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      const isIntersecting = entries.some(({ isIntersecting: visible }) => visible)
      const becameVisible = isIntersecting && !isSentinelIntersecting
      isSentinelIntersecting = isIntersecting

      if (
        !becameVisible
        || isGalleryLoading.value
        || (!hasMoreHistory.value && !hasPendingFiles.value)
      ) {
        return
      }

      void loadOlder()
    },
    {
      root,
      rootMargin: '240px 0px',
      threshold: 0,
    },
  )
  loadMoreObserver.observe(loadMoreSentinelRef.value)
}

watch(tabsForView, () => {
  if (!tabsForView.value.length) {
    return
  }

  if (!tabsForView.value.includes(fileTypeForView.value)) {
    fileTypeForView.value = tabsForView.value[0]!
  }
}, { immediate: true })

watch(fileTypeForView, (type) => {
  if (type !== GalleryTypes.LINK) {
    linksSearch.value = ''
  }
  if (type !== GalleryTypes.VIDEO) {
    videosSearch.value = ''
  }
  if (type !== GalleryTypes.FILE) {
    filesSearch.value = ''
  }
  hideMediaMenu()
  activeMediaItem.value = null
  isSentinelIntersecting = false
  void nextTick(() => {
    scrollToActiveTab()
    setupLoadMoreObserver()
  })
}, { immediate: true })

watch(
  [
    loadMoreSentinelRef,
    attachmentsScrollRef,
    hasMoreHistory,
    hasPendingFiles,
    () => groupedAttachments.value.length > 0,
  ],
  () => {
    void nextTick(setupLoadMoreObserver)
  },
  { immediate: true },
)

watch([showStickyDateHeader, groupedAttachments, stickyChromeRef, attachmentsScrollRef], () => {
  void nextTick(setupStickyDateScroll)
}, { immediate: true })

onMounted(() => {
  void nextTick(() => {
    setupLoadMoreObserver()
    setupStickyDateScroll()
  })
})

onBeforeUnmount(() => {
  loadMoreObserver?.disconnect()
  loadMoreObserver = null
  detachStickyDateScroll?.()
  detachStickyDateScroll = null
  hideMediaMenu()
})
</script>
